import { useEffect, useMemo, useRef, useState } from "react";
import { formatDuration } from "@/lib/bkls-marketplace";

interface Props {
  src: string;
  title: string;
  peaks?: number[];
  durationSeconds?: number | null;
  onPlay?: () => void;
  onEnded?: () => void;
}

/**
 * Accessible waveform preview player.
 *
 * The waveform itself is decorative (aria-hidden); all control is through a
 * real button and a real range input, so keyboard and screen-reader users get
 * the same functionality without needing to interpret the bars.
 */
export function WaveformPlayer({ src, title, peaks, durationSeconds, onPlay, onEnded }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(durationSeconds ?? 0);

  // A stable fallback shape when no measured envelope exists, so the control
  // still reads as an audio scrubber rather than an empty strip.
  const bars = useMemo(() => {
    if (peaks?.length) return peaks;
    return Array.from({ length: 64 }, (_, i) => 0.25 + 0.2 * Math.abs(Math.sin(i / 3)));
  }, [peaks]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setPosition(el.currentTime);
    const onMeta = () => setDuration(el.duration || durationSeconds || 0);
    const onEnd = () => {
      setPlaying(false);
      setPosition(0);
      onEnded?.();
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnd);
    };
  }, [durationSeconds, onEnded]);

  async function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      try {
        await el.play();
        setPlaying(true);
        onPlay?.();
      } catch {
        setPlaying(false);
      }
    } else {
      el.pause();
      setPlaying(false);
    }
  }

  const progress = duration > 0 ? position / duration : 0;

  return (
    <div className="rounded-2xl border hairline bg-obsidian/60 p-4">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={toggle}
          aria-pressed={playing}
          aria-label={playing ? `Pause preview of ${title}` : `Play preview of ${title}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold text-gold-luminous transition-colors hover:bg-gold hover:text-obsidian focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
        >
          <span aria-hidden="true" className="text-sm">
            {playing ? "❚❚" : "▶"}
          </span>
        </button>

        <div className="min-w-0 flex-1">
          <div aria-hidden="true" className="flex h-10 items-end gap-[2px]">
            {bars.map((p, i) => {
              const played = i / bars.length <= progress;
              return (
                <span
                  key={i}
                  className={played ? "flex-1 rounded-sm bg-gold" : "flex-1 rounded-sm bg-foreground/20"}
                  style={{ height: `${Math.max(8, Math.round(p * 100))}%` }}
                />
              );
            })}
          </div>
          <label className="sr-only" htmlFor={`seek-${title}`}>
            Seek within the preview of {title}
          </label>
          <input
            id={`seek-${title}`}
            type="range"
            min={0}
            max={Math.max(1, Math.floor(duration))}
            step={1}
            value={Math.floor(position)}
            onChange={(e) => {
              const el = audioRef.current;
              if (!el) return;
              el.currentTime = Number(e.target.value);
              setPosition(Number(e.target.value));
            }}
            className="mt-2 w-full accent-[hsl(var(--gold,45_80%_60%))]"
          />
        </div>

        <p className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {formatDuration(duration - position) ?? formatDuration(durationSeconds) ?? "—"}
        </p>
      </div>

      <audio ref={audioRef} src={src} preload="none" className="sr-only">
        <track kind="captions" />
      </audio>
      <p className="mt-3 text-xs text-muted-foreground">Tagged preview. Licensed files are untagged.</p>
    </div>
  );
}
